import { useState } from "react";
import { Plus, Tag } from "lucide-react";
import type { Discipline } from "@/domains/pm/types";
import { DisciplineBadges } from "./DisciplineBadges";
import { FittedDisciplineBadges } from "./FittedDisciplineBadges";
import { DisciplineMultiSelectList } from "./DisciplineMultiSelectList";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DisciplinePickerProps {
  all: Discipline[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** Placeholder shown in the trigger when nothing is selected. */
  emptyLabel: string;
  /** Sizing override for the trigger button. Defaults to a full-width
   *  form control; the meta bar passes a compact variant. */
  triggerClassName?: string;
  /** Size of the selected discipline badges + empty-state label. `lg` enlarges
   *  them ~35% for the expanded issue panel's meta bar. */
  badgeSize?: "sm" | "md" | "lg";
  /** Render the selected badges as quiet outline pills (WHA-3810). */
  badgeVariant?: "solid" | "outline";
  /** Show a trailing "+" add affordance instead of the tag/empty label — the
   *  issue detail panel labels the group itself, so the trigger only needs to
   *  say "add one" (WHA-3810). */
  addAffordance?: boolean;
  /** Read the tags without being able to change them — snapshot mode. */
  disabled?: boolean;
  /** Spell the tags out when they fit the trigger's full width, else show
   *  the codes. The trigger then spans its container, since that is the room. */
  fitLabels?: boolean;
  /** With `fitLabels`, the spacing between pills (the pills sit in their own
   *  rows then, so the trigger's gap does not reach them). */
  badgeRowClassName?: string;
}

/**
 * DisciplinePicker — a dropdown discipline multi-select: a bordered trigger
 * showing the selected gray badges, opening a Popover with the shared
 * checklist. Used by the expanded-view meta bar and the New Issue modal so
 * both edit disciplines the same way (WHA-3449).
 */
export function DisciplinePicker({
  all,
  selectedIds,
  onChange,
  emptyLabel,
  triggerClassName,
  badgeSize = "sm",
  badgeVariant = "solid",
  addAffordance = false,
  disabled = false,
  fitLabels = false,
  badgeRowClassName,
}: DisciplinePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = all.filter((d) => selectedIds.includes(d.id));
  const isLg = badgeSize === "lg";
  const addMark = (
    <span
      aria-label={emptyLabel}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground"
    >
      <Plus className="h-3 w-3" />
    </span>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex flex-wrap items-center gap-1 rounded-md text-left hover:bg-accent focus:outline-none focus:ring-1 focus:ring-ring",
            triggerClassName ?? "min-h-9 w-full border border-input px-2 py-1",
            fitLabels && "w-full"
          )}
        >
          {selected.length === 0 && !addAffordance ? (
            <span
              className={cn(
                "flex items-center gap-1.5 text-muted-foreground",
                isLg ? "text-[15px]" : "text-xs"
              )}
            >
              <Tag className={isLg ? "h-[18px] w-[18px]" : "h-3.5 w-3.5"} />
              {emptyLabel}
            </span>
          ) : fitLabels && selected.length > 0 ? (
            <FittedDisciplineBadges
              disciplines={selected}
              size={badgeSize}
              variant={badgeVariant}
              rowClassName={badgeRowClassName}
              trailing={addAffordance && addMark}
            />
          ) : (
            <>
              <DisciplineBadges disciplines={selected} size={badgeSize} variant={badgeVariant} />
              {addAffordance && addMark}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1" align="start">
        {/* Shared checklist — same component the right-click context menu
            and the issue-row inline editor use. */}
        <DisciplineMultiSelectList
          disciplines={all}
          selectedIds={selectedIds}
          onChange={onChange}
          emptyMessage={emptyLabel}
        />
      </PopoverContent>
    </Popover>
  );
}
